import type { HelpArticle } from '@core/services/help';

/**
 * "Se o erro continuar" (§3.2 item 8, KB-ERR-05) no painel de ajuda.
 *
 * Paridade com o site (tarefa E5): o texto e a regra de escolha da variante
 * repetem `helpErrorFollowup` de src/lib/help-markdown.ts do maisgestao-site
 * (commit f5c1812). O texto não vem no JSON exportado; ao mudar uma frase no
 * site, mude aqui também (o spec `help-error-followup.pure.spec.ts` fixa as
 * duas cópias no mesmo texto).
 */
export type HelpErrorFollowupVariant = 'nfse' | 'terceiro' | 'sistema' | 'sintoma';

export interface HelpErrorFollowup {
  variant: HelpErrorFollowupVariant;
  title: string;
  intro: string;
  items: readonly string[];
  notes: readonly string[];
}

/**
 * Campos do artigo que decidem o bloco. Aceita `null` e valores fora da
 * união porque o catálogo chega do JSON sem validar estes campos.
 */
export interface HelpErrorFollowupSource {
  type?: HelpArticle['type'] | string | null;
  errorSource?: HelpArticle['errorSource'] | string | null;
  category: string;
  /** Título do artigo: fonte dos códigos E#### enquanto `errorCodes` não vem no JSON. */
  title?: string | null;
  /** Códigos do erro (`errorCodes` do site), quando o JSON exportado os trouxer. */
  errorCodes?: readonly unknown[] | null;
  /** Só a versão importa aqui: é o leiaute citado na frase do código nacional. */
  officialSource?: { version?: unknown } | null;
}

const ERROR_CATEGORIES = new Set<string>(['erros-nfse', 'erros-recebimentos']);
const FOLLOWUP_TITLE = 'Se o erro continuar';
const NO_ACCESS_NOTE = 'Se faltar acesso à tela de correção, peça ajuda a um administrador da empresa.';

const FOLLOWUPS: Readonly<Record<HelpErrorFollowupVariant, Omit<HelpErrorFollowup, 'variant' | 'title'>>> = {
  nfse: {
    intro: 'Envie estes dados ao suporte ou à contabilidade:',
    items: [
      'o código do erro, como E0160, quando houver;',
      'a mensagem completa, que aparece em Ver erro completo;',
      'a competência da nota;',
      'o CNPJ do emitente;',
      'a conta a receber da nota;',
      'a data e o horário da tentativa.',
    ],
    notes: [
      'Se não souber se a nota foi emitida, fale com o suporte antes de gerar outra.',
      'Enquadramento, situação cadastral, município e competência são confirmados com a contabilidade.',
      NO_ACCESS_NOTE,
      'Nunca envie senhas, o certificado digital nem a senha dele.',
    ],
  },
  terceiro: {
    intro: 'Envie estes dados ao suporte:',
    items: [
      'o provedor que mostrou a mensagem, como PJBank ou Transfeera;',
      'a mensagem completa, como aparece na tela;',
      'a conta a receber ou o número da cobrança, da coluna Número único;',
      'o CNPJ da empresa;',
      'a data e o horário da tentativa.',
    ],
    notes: [
      'Se não souber se a cobrança foi criada, fale com o suporte antes de emitir outra.',
      NO_ACCESS_NOTE,
      'Nunca envie senhas nem chaves do provedor.',
    ],
  },
  sistema: {
    intro: 'Envie estes dados ao suporte:',
    items: [
      'a mensagem completa, como aparece na tela;',
      'a tela em que ela apareceu e o que você estava fazendo;',
      'o CNPJ da empresa;',
      'a data e o horário da tentativa.',
    ],
    notes: [NO_ACCESS_NOTE, 'Nunca envie senhas.'],
  },
  sintoma: {
    intro: 'Envie estes dados ao suporte:',
    items: [
      'a tela em que o problema aparece;',
      'o que você esperava ver e o que apareceu;',
      'um exemplo afetado, como a conta, a cobrança ou o lançamento;',
      'o CNPJ da empresa;',
      'a data e o horário em que notou o problema.',
    ],
    notes: [NO_ACCESS_NOTE, 'Nunca envie senhas.'],
  },
};

/**
 * Variante do bloco, ou `null` quando o artigo não é de erro. Até a migração
 * preencher `type`, as categorias `erros-nfse` e `erros-recebimentos` contam
 * como erro. Na categoria de NFS-e valem os dados da nota; na de recebimentos,
 * os do provedor; fora delas, decide `errorSource`.
 */
export function helpErrorFollowup(article: HelpErrorFollowupSource): HelpErrorFollowup | null {
  const semTipo = article.type === undefined || article.type === null;
  const isError = semTipo ? ERROR_CATEGORIES.has(article.category) : article.type === 'erro';
  if (!isError) return null;
  const variant: HelpErrorFollowupVariant =
    article.errorSource === 'nfse-nacional' || article.category === 'erros-nfse' ? 'nfse'
      : article.errorSource === 'terceiro' || article.category === 'erros-recebimentos' ? 'terceiro'
        : article.errorSource === 'sintoma' ? 'sintoma'
          : 'sistema';
  return { variant, title: FOLLOWUP_TITLE, ...FOLLOWUPS[variant] };
}

/**
 * Canais do suporte no bloco "Se o erro continuar". O site aponta o WhatsApp
 * (`URLS.whatsapp`, o mesmo número); dentro do sistema há também o chamado,
 * aberto com a tela de origem (`?de=`), como no painel "Ajuda e suporte".
 */
export const SUPORTE_WHATSAPP_URL = 'https://wa.me/553121802122';
export const ROTA_NOVO_CHAMADO = '/painel/suporte/novo';

// ── Código nacional não é código municipal (§3.2, KB-ERR-05) ───────────────

/**
 * Versão do Anexo I importado no site (`NFSE_CODIGOS_FONTE.versao` em
 * src/lib/help-nfse-codigos.ts): vale quando o artigo não traz uma versão
 * numérica em `officialSource`.
 */
export const NFSE_CODIGOS_VERSAO = '1.01';

const NFSE_NATIONAL_CODE = /^E\d{4}$/i;
const NFSE_NATIONAL_CODE_IN_TEXT = /\bE\d{4}\b/gi;

function listaPtBr(itens: readonly string[]): string {
  return itens.length < 2 ? itens.join('') : `${itens.slice(0, -1).join(', ')} e ${itens[itens.length - 1]}`;
}

/**
 * A frase do código nacional, igual a `helpNfseCodeScope` de
 * src/lib/help-markdown.ts do maisgestao-site: o código E#### é do Sistema
 * Nacional NFS-e, e o padrão ABRASF usa outra numeração. `null` quando o
 * artigo não é `nfse-nacional` (até a migração, a categoria `erros-nfse`) ou
 * não cita código E####. Os códigos vêm de `errorCodes` ou, sem eles, do
 * título; a versão, de `officialSource` ou do Anexo I importado.
 */
export function helpNfseCodeScope(article: HelpErrorFollowupSource): string | null {
  const semOrigem = article.errorSource === undefined || article.errorSource === null;
  const nacional = semOrigem ? article.category === 'erros-nfse' : article.errorSource === 'nfse-nacional';
  if (!nacional) return null;
  const declarados = (Array.isArray(article.errorCodes) ? article.errorCodes : [])
    .filter((codigo): codigo is string => typeof codigo === 'string' && NFSE_NATIONAL_CODE.test(codigo));
  const titulo = typeof article.title === 'string' ? article.title : '';
  const achados = declarados.length ? declarados : Array.from(titulo.matchAll(NFSE_NATIONAL_CODE_IN_TEXT), achado => achado[0]);
  const codigos = [...new Set(achados.map(codigo => codigo.toUpperCase()))];
  if (!codigos.length) return null;
  const versaoOficial = typeof article.officialSource?.version === 'string' ? article.officialSource.version.trim() : '';
  const versao = /^\d+(?:\.\d+)*$/.test(versaoOficial) ? versaoOficial : NFSE_CODIGOS_VERSAO;
  return `${codigos.length === 1 ? 'Código' : 'Códigos'} ${listaPtBr(codigos)} do Sistema Nacional NFS-e (leiaute ${versao}). `
    + 'Sistemas municipais no padrão ABRASF usam outra numeração.';
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const DATA_POR_EXTENSO = new Intl.DateTimeFormat('pt-BR', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });

/** Data válida no formato AAAA-MM-DD, ou `null`. */
export function dataIsoDaAjuda(valor: unknown): string | null {
  if (typeof valor !== 'string' || !ISO_DATE.test(valor)) return null;
  const data = new Date(`${valor}T12:00:00Z`);
  return !Number.isNaN(data.getTime()) && data.toISOString().slice(0, 10) === valor ? valor : null;
}

/**
 * Igual a `formatHelpDate` do site: AAAA-MM-DD por extenso em pt-BR
 * ("15 de janeiro de 2026"). Qualquer outro valor fica como veio, como no site.
 */
export function formatarDataDaAjuda(valor: string): string {
  const iso = dataIsoDaAjuda(valor);
  return iso ? DATA_POR_EXTENSO.format(new Date(`${iso}T12:00:00Z`)) : valor;
}
